import { ChevronDown, ChevronUp } from "lucide-react";
import type { FormEvent, ReactNode } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { EntryMiniTransaction } from "@/shared/cache";
import { useRestrictedWords, useSettingsCache } from "@/shared/cache";
import {
	detectRestrictedWords,
	mergeOverlappingMatches,
} from "@/shared/utils/restrictedWords";
import {
	isValidUnitsForMatter,
	isZurichMatter,
	STANDARD_UNITS_ERROR,
	snapUnitsForMatter,
	stepUnitsDown,
	stepUnitsUp,
	ZURICH_UNITS_ERROR,
} from "@/shared/utils/zurich";
import { NarrativeField } from "@/v2/components/entry-form/NarrativeField";
import { TimePicker } from "@/v2/components/time-picker/TimePicker";
import { Button } from "@/v2/components/ui/button";
import { Input } from "@/v2/components/ui/input";
import { Label } from "@/v2/components/ui/label";
import {
	ResponsiveDialog,
	ResponsiveDialogContent,
} from "@/v2/components/ui/responsive-dialog";
import { cn } from "@/v2/lib/utils";

export interface SourceEntryEditValues {
	duration: number;
	time: string;
	description: string;
}

interface SourceEntryEditDialogProps {
	entry: EntryMiniTransaction | null;
	matterId?: string | null;
	matterClient?: string | null;
	/** When set, the group's timer is running: locks units + time and shows this note. */
	activeTimerReadOnlyNote?: ReactNode;
	onOpenChange: (open: boolean) => void;
	onSubmit: (
		entry: EntryMiniTransaction,
		values: SourceEntryEditValues,
	) => void;
}

function restrictedWordScope(matterId: string | null | undefined): {
	client: string | undefined;
	matter: string | undefined;
} {
	if (!matterId) return { client: undefined, matter: undefined };
	const [client, ...matterParts] = matterId.split(".");
	return { client, matter: matterParts.join(".") || undefined };
}

export function SourceEntryEditDialog({
	entry,
	matterId,
	matterClient,
	activeTimerReadOnlyNote,
	onOpenChange,
	onSubmit,
}: SourceEntryEditDialogProps) {
	const timerLocked = Boolean(activeTimerReadOnlyNote);
	const [duration, setDuration] = useState(entry?.units ?? 0);
	const [durationInput, setDurationInput] = useState(
		entry ? String(entry.units) : "",
	);
	const [durationError, setDurationError] = useState<string | null>(null);
	const [time, setTime] = useState(entry?.time ?? "00:00");
	const [description, setDescription] = useState(entry?.summary ?? "");
	const [descriptionError, setDescriptionError] = useState<string | null>(null);
	const sourceSubtitle = entry?.canonicalName?.trim() || undefined;

	const { settings } = useSettingsCache();
	const { getRestrictedWords } = useRestrictedWords();
	const restrictedWordsScope = useMemo(
		() => restrictedWordScope(matterId),
		[matterId],
	);
	const restrictedWords = useMemo(() => {
		if (!settings.check_for_red_words) return [];
		return getRestrictedWords(
			restrictedWordsScope.client,
			restrictedWordsScope.matter,
		);
	}, [settings.check_for_red_words, getRestrictedWords, restrictedWordsScope]);

	useEffect(() => {
		setDuration(entry?.units ?? 0);
		setDurationInput(entry ? String(entry.units) : "");
		setDurationError(null);
		setTime(entry?.time ?? "00:00");
		setDescription(entry?.summary ?? "");
		setDescriptionError(null);
	}, [entry]);

	const unitError = useCallback(
		(value: number) => {
			if (!isValidUnitsForMatter(value, matterClient)) {
				return isZurichMatter(matterClient)
					? ZURICH_UNITS_ERROR
					: STANDARD_UNITS_ERROR;
			}
			return null;
		},
		[matterClient],
	);

	const commitDurationInput = useCallback(() => {
		const parsed = Number.parseFloat(durationInput);
		if (!Number.isFinite(parsed) || parsed <= 0) {
			const fallback = snapUnitsForMatter(0, matterClient);
			setDuration(fallback);
			setDurationInput(String(fallback));
			setDurationError(null);
			return fallback;
		}

		const snapped = snapUnitsForMatter(parsed, matterClient);
		setDuration(snapped);
		setDurationInput(String(snapped));
		setDurationError(null);
		return snapped;
	}, [durationInput, matterClient]);

	const changeDuration = useCallback((next: number) => {
		setDuration(next);
		setDurationInput(String(next));
		setDurationError(null);
	}, []);

	const handleSubmit = useCallback(
		(event: FormEvent<HTMLFormElement>) => {
			event.preventDefault();
			if (!entry) return;

			const nextDuration = commitDurationInput();
			const nextDurationError = unitError(nextDuration);
			if (nextDurationError) {
				setDurationError(nextDurationError);
				return;
			}

			const nextDescription = description.trim();
			if (!nextDescription) {
				setDescriptionError("Summary is required");
				return;
			}

			const restrictedMatches = mergeOverlappingMatches(
				detectRestrictedWords(nextDescription, restrictedWords),
			);
			if (restrictedMatches.some((match) => match.severity === "block")) {
				setDescriptionError("Please fix restricted words before saving");
				return;
			}

			onSubmit(entry, {
				duration: nextDuration,
				time,
				description: nextDescription,
			});
		},
		[
			commitDurationInput,
			description,
			entry,
			onSubmit,
			restrictedWords,
			time,
			unitError,
		],
	);

	return (
		<ResponsiveDialog open={entry !== null} onOpenChange={onOpenChange}>
			<ResponsiveDialogContent
				className="!max-h-[90vh]"
				title="Edit Source Entry"
				subtitle={sourceSubtitle}
				headerClassName="p-4 pb-2"
				footer={
					<div className="flex justify-end gap-2">
						<Button
							type="button"
							variant="outline"
							onClick={() => onOpenChange(false)}
						>
							Cancel
						</Button>
						<Button type="submit" form="source-entry-edit-form">
							Save
						</Button>
					</div>
				}
			>
				<form
					id="source-entry-edit-form"
					onSubmit={handleSubmit}
					className="grid gap-4 p-4"
				>
					<div className="grid w-full grid-cols-2 gap-3">
						<div className="grid min-w-0 content-start gap-1.5">
							<Label id="source-entry-time-label" htmlFor={timerLocked ? undefined : "source-entry-time"}>Time</Label>
							{timerLocked ? (
								<p
									aria-labelledby="source-entry-time-label"
									className="flex h-10 items-center rounded-md bg-muted/50 px-3 text-base text-muted-foreground"
								>
									{time}
								</p>
							) : (
								<TimePicker
									id="source-entry-time"
									value={time}
									onChange={setTime}
									triggerClassName="bg-card border-border"
								/>
							)}
							{timerLocked ? (
								<p className="mt-1 text-xs text-muted-foreground">
									{activeTimerReadOnlyNote}
								</p>
							) : null}
						</div>

						<div className="grid min-w-0 gap-1.5">
							<Label id="source-entry-units-label" htmlFor={timerLocked ? undefined : "source-entry-units"}>Units</Label>
							{timerLocked ? (
								<p
									aria-labelledby="source-entry-units-label"
									className="flex h-10 items-center rounded-md bg-muted/50 px-3 text-base text-muted-foreground"
								>
									{durationInput || "0"}
								</p>
							) : (
							<div className="flex gap-1">
								<Input
									id="source-entry-units"
									type="text"
									inputMode="decimal"
									autoComplete="off"
									value={durationInput}
									onChange={(event) => {
										setDurationInput(event.target.value);
										setDurationError(null);
									}}
									onBlur={commitDurationInput}
									onKeyDown={(event) => {
										if (event.key === "ArrowUp") {
											event.preventDefault();
											changeDuration(stepUnitsUp(duration, matterClient));
										}
										if (event.key === "ArrowDown") {
											event.preventDefault();
											changeDuration(stepUnitsDown(duration, matterClient));
										}
									}}
									className={cn(
										"text-foreground bg-card border-border",
										durationError && "border-red-500",
									)}
								/>
								<div className="flex flex-col gap-0.5">
									<Button
										type="button"
										variant="outline"
										size="icon"
										onClick={() =>
											changeDuration(stepUnitsUp(duration, matterClient))
										}
										className="h-4.5 w-7 p-0 text-foreground bg-card border-border hover:bg-muted"
										tabIndex={-1}
									>
										<ChevronUp className="h-3 w-3" />
									</Button>
									<Button
										type="button"
										variant="outline"
										size="icon"
										onClick={() =>
											changeDuration(stepUnitsDown(duration, matterClient))
										}
										className="h-4.5 w-7 p-0 text-foreground bg-card border-border hover:bg-muted"
										tabIndex={-1}
									>
										<ChevronDown className="h-3 w-3" />
									</Button>
								</div>
							</div>
							)}
							{timerLocked ? (
								<p className="mt-1 text-xs text-muted-foreground">
									{activeTimerReadOnlyNote}
								</p>
							) : null}
							{durationError && (
								<p className="text-xs text-red-500">{durationError}</p>
							)}
						</div>
					</div>

					<div className="grid w-full gap-1.5">
						<Label htmlFor="description">Summary</Label>
						<NarrativeField
							value={description}
							onChange={(event) => {
								setDescription(event.target.value);
								setDescriptionError(null);
							}}
							error={descriptionError ?? undefined}
							restrictedWords={restrictedWords}
						/>
					</div>
				</form>
			</ResponsiveDialogContent>
		</ResponsiveDialog>
	);
}
